import { Injectable, inject } from '@angular/core';
import type { PhotoSortMode } from '../entities/gallery-manage.models';
import type {
  AddVideoResult,
  DeleteSceneResult,
  GalleryManageDataPort,
  PhotoDeleteResult,
  ScenesResult,
  UpdateVideoResult,
} from './gallery-manage.data';
import { GalleryManageApiService } from './gallery-manage-api.service';

@Injectable({ providedIn: 'root' })
export class HttpGalleryManageData implements GalleryManageDataPort {
  private readonly api = inject(GalleryManageApiService);

  scenes(galleryId: string): Promise<ScenesResult> {
    return this.api.getScenes(galleryId);
  }

  movePhotos(
    galleryId: string,
    photoIds: readonly string[],
    targetSceneId: string,
    targetOrderedIds: readonly string[],
    toIndex: number,
  ): Promise<boolean> {
    return this.api.movePhotosToPosition(galleryId, photoIds, targetSceneId, targetOrderedIds, toIndex);
  }

  movePhotosToGallery(galleryId: string, photoIds: readonly string[], targetGalleryId: string): Promise<boolean> {
    return this.api.movePhotosToGallery(galleryId, photoIds, targetGalleryId);
  }

  deletePhotos(galleryId: string, photoIds: readonly string[], confirmCover?: boolean): Promise<PhotoDeleteResult> {
    return this.api.deletePhotos(galleryId, photoIds, confirmCover);
  }

  reprocessPhoto(photoId: string): Promise<boolean> {
    return this.api.reprocessPhoto(photoId);
  }

  reorderScenes(galleryId: string, orderedIds: readonly string[]): Promise<boolean> {
    return this.api.reorderScenes(galleryId, orderedIds);
  }

  setVideoPoster(videoId: string, photoId: string | null): Promise<boolean> {
    return this.api.setVideoPoster(videoId, photoId);
  }

  addScene(galleryId: string, name?: string): Promise<string | null> {
    return this.api.addScene(galleryId, name);
  }

  addVideo(galleryId: string, sceneId: string, url: string, name?: string): Promise<AddVideoResult> {
    return this.api.addVideo(galleryId, sceneId, url, name);
  }

  addVideoFile(galleryId: string, sceneId: string, fileUrl: string, fileId?: string, name?: string): Promise<AddVideoResult> {
    return this.api.addVideoFile(galleryId, sceneId, fileUrl, fileId, name);
  }

  updateVideo(sceneId: string, url: string, name: string): Promise<UpdateVideoResult> {
    return this.api.updateVideo(sceneId, url, name);
  }

  renameScene(sceneId: string, name: string): Promise<boolean> {
    return this.api.renameScene(sceneId, name);
  }

  setSceneHidden(sceneId: string, isHidden: boolean): Promise<boolean> {
    return this.api.setSceneHidden(sceneId, isHidden);
  }

  setGallerySortMode(galleryId: string, sortMode: PhotoSortMode): Promise<boolean> {
    return this.api.setGallerySortMode(galleryId, sortMode);
  }

  deleteScene(sceneId: string): Promise<DeleteSceneResult> {
    return this.api.deleteScene(sceneId);
  }

  setSceneDescription(sceneId: string, description: string): Promise<boolean> {
    return this.api.setSceneDescription(sceneId, description);
  }

  clearSceneDescription(sceneId: string): Promise<boolean> {
    return this.api.clearSceneDescription(sceneId);
  }
}
